import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import { useBreadcrumbs } from '../../../layout/breadcrumbsContext/useBreadcrumbs/useBreadcrumbs'
import { DONATION_COLOUR_MAP } from '../../../dashboard/insightsColourMap'
import { ReportCard, reportsBasePath } from '../../../reports/reportsConfig'

import { DatasetColumnConfig, Insights, toCsvFileName } from '@percent/domain/insights'
import { Flex, Heading, Icon, Text } from '@percent/lemonade'
import { useAuthState } from '@percent/partner-dashboard/common/hooks'
import { PartnerScopes } from '@percent/partner-dashboard/constants/scopes'
import { useServices } from '@percent/partner-dashboard/context/serviceContext/ServiceContext'

type EmployeeDonationsReportProps = {
  report: ReportCard
}

/**
 * The employee-donations report — owns everything specific to it: the donation
 * colour map, the dataset column config (the table reads column labels/types from
 * the response; this only orders columns, pins the identifier, relabels
 * donor→employee and hosted→employee contribution), and the static composition of
 * cards + charts + table inside its own `Insights.Domain`. The `giving` domain is
 * already scoped to employee giving at the table level (giving_donations), so no
 * category lock is applied here.
 */
export function EmployeeDonationsReport({ report }: Readonly<EmployeeDonationsReportProps>) {
  const { t } = useTranslation()
  const { insightsService } = useServices()
  const { partner } = useAuthState()

  // The wallet KPI card is only meaningful for partners with giving wallets
  // enabled (NEX-789); hide it otherwise, mirroring the donation-type filter's
  // wallet gate (useDonationTypeFilter).
  const hasWalletScope = partner?.scopes?.some(scope => scope.scopeName === PartnerScopes.DonationWallet)

  const heading = t(report.titleKey)
  const subheading = t(report.descriptionKey)

  useBreadcrumbs([{ label: t('breadcrumb.reports'), path: reportsBasePath('workplace-giving') }, { label: heading }])

  const emptyState = useMemo(
    () => (
      <>
        <Icon name="empty" size={6} color="goodstack" />
        <Text size="small">{t('typography.dashboard.noDataToShow')}</Text>
      </>
    ),
    [t]
  )
  const errorLabel = t('typography.dashboard.failedToLoad')

  const datasetColumns = useMemo<DatasetColumnConfig[]>(
    () => [
      { id: 'employeeName', isSticky: true, width: '180px', header: t('reports.donations.table.employeeName') },
      { id: 'employeeEmail', width: '320px', header: t('reports.donations.table.employeeEmail') },
      { id: 'donationAmount', header: t('reports.donations.table.amount') },
      {
        id: 'donationType',
        valueLabelOverrides: { hosted: t('reports.donations.table.type.employeeContribution') }
      },
      { id: 'organisationName' },
      { id: 'organisationCountry' },
      { id: 'organisationRegion' },
      { id: 'employeeCountry' },
      { id: 'employeeRegion' }
    ],
    [t]
  )

  const filterLabels = {
    reset: t('typography.dashboard.filters.reset'),
    all: t('typography.dashboard.filters.all'),
    optionsError: t('typography.dashboard.filters.optionsLoadError'),
    dateRange: {
      label: t('typography.dashboard.filters.dateRange'),
      allTime: t('typography.dashboard.filters.allTime'),
      custom: t('typography.dashboard.filters.preset.custom'),
      apply: t('typography.dashboard.filters.apply'),
      clear: t('typography.dashboard.filters.clear')
    }
  }

  const exportLabels = {
    trigger: t('reports.donations.export.trigger'),
    title: t('reports.donations.export.title', { report: heading }),
    description: t('reports.donations.export.description'),
    appliedFiltersHeading: t('reports.donations.export.appliedFilters'),
    noFiltersApplied: t('reports.donations.export.noFilters'),
    columnsLabel: t('reports.donations.export.columns'),
    modeDefault: (count: number) => t('reports.donations.export.mode.default', { count }),
    modeAll: (count: number) => t('reports.donations.export.mode.all', { count }),
    modeCustom: t('reports.donations.export.mode.custom'),
    includes: t('reports.donations.export.includes'),
    zeroColumnsError: t('reports.donations.export.zeroColumns'),
    cancel: t('button.cancel'),
    export: t('reports.donations.export.confirm'),
    exportSuccess: (rowCount: number) => t('reports.donations.export.success', { count: rowCount }),
    exportError: t('reports.donations.export.error'),
    exportTooLarge: t('reports.donations.export.tooLarge'),
    exportDateRangeTooLarge: t('reports.donations.export.dateRangeTooLarge'),
    exportUnavailable: t('reports.donations.export.unavailable'),
    close: t('button.close'),
    emptyTitle: t('reports.donations.export.empty.title'),
    emptyFilteredDescription: t('reports.donations.export.empty.filtered'),
    emptyNoDataDescription: t('reports.donations.export.empty.noData')
  }

  return (
    <Insights.Domain domain={report.domain} service={insightsService} colourMap={DONATION_COLOUR_MAP}>
      <Flex
        direction="column"
        gap={24}
        w="100%"
        p={24}
        bg="white"
        style={{ borderRadius: 8 }}
        data-testid="report-detail"
      >
        <Flex direction="column" gap="xs">
          <Heading level="h2">{heading}</Heading>
          <Text size="small">{subheading}</Text>
        </Flex>

        {/* Own row so filter-bar width changes don't shift the heading (NEX-754). */}
        <Insights.Filters testId="report-filters" labels={filterLabels} dropdownAlign="start" />

        {/* The five headline KPI cards (NEX-689), in spec order. Each card's heading
            and subtitle come from the metric response (BE-resolved DisplayText); the
            report only names the metric. */}
        <Flex gap={16}>
          <Insights.Stat metric="total-hosted" errorLabel={errorLabel} testId="report-stat-total-hosted" />
          <Insights.Stat metric="total-matched" errorLabel={errorLabel} testId="report-stat-total-matched" />
          {hasWalletScope && (
            <Insights.Stat metric="total-wallet" errorLabel={errorLabel} testId="report-stat-total-wallet" />
          )}
          <Insights.Stat metric="donation-count" errorLabel={errorLabel} testId="report-stat-donation-count" />
          <Insights.Stat metric="unique-donors" errorLabel={errorLabel} testId="report-stat-unique-donors" />
        </Flex>

        <Flex gap={16}>
          <Insights.LineChart
            metric="over-time"
            emptyState={emptyState}
            errorLabel={errorLabel}
            testId="report-chart-over-time"
          />
          <Insights.DonutChart
            metric="by-type"
            emptyState={emptyState}
            errorLabel={errorLabel}
            testId="report-chart-by-type"
          />
        </Flex>

        <Insights.Dataset
          dataset="donations"
          columns={datasetColumns}
          title={t('reports.donations.table.title')}
          action={
            <Insights.Export
              dataset="donations"
              defaultColumns={datasetColumns}
              fileName={toCsvFileName(heading)}
              labels={exportLabels}
              testId="report-export"
            />
          }
          pageSizeSelector={{ options: [10, 20, 50], label: t('typography.resultsOnPage') }}
          labels={{
            empty: t('reports.donations.table.empty'),
            error: errorLabel,
            results: count => t('reports.donations.table.results', { count })
          }}
          testId="report-dataset"
        />
      </Flex>
    </Insights.Domain>
  )
}
